import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  ChevronUp, ChevronDown, Send, RefreshCw, Trash2, MapPin, Clock, User,
  Truck, Wand2, Package, AlertTriangle, Phone, GripVertical,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const variantLabel = { strict: '🎯 Строгий', flex: '🚀 Оптимальный', fast: '⚡ Быстрый' };
const UNASSIGNED = '__unassigned__';

export const RouteEditorPanel = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [targetDate, setTargetDate] = useState(formatDateInput(tomorrow));
  const [variants, setVariants] = useState({ strict: [], flex: [], fast: [] });
  const [stats, setStats] = useState({});
  const [strictUserIds, setStrictUserIds] = useState([]);
  const [activeVariant, setActiveVariant] = useState('flex');
  const [vehicles, setVehicles] = useState([]);
  // assignment: { [vehicleId | UNASSIGNED]: [stop, ...] }
  const [assignment, setAssignment] = useState({});
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // ───── Drag & drop state ─────
  // Source of the stop currently being dragged: { colId, idx, orderId }
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);   // order_id being dragged (visual)
  const [dragOverCol, setDragOverCol] = useState(null); // column highlighted as drop target

  // First (default) vehicle = lowest sort_order (e.g. Changan)
  const defaultVehicleId = vehicles.length ? vehicles[0].id : UNASSIGNED;

  const initAssignment = useCallback((stops, vehs) => {
    const cols = {};
    const firstId = vehs.length ? vehs[0].id : UNASSIGNED;
    vehs.forEach((v) => { cols[v.id] = []; });
    cols[UNASSIGNED] = [];
    cols[firstId] = [...stops];
    return cols;
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/vehicles`, { headers: authHeaders() });
      return res.data.vehicles || [];
    } catch (e) {
      toast.error('Не удалось загрузить машины (Автопарк)');
      return [];
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const [previewRes, vehs] = await Promise.all([
        axios.get(`${API}/api/admin/logistics/route-editor/preview`, {
          headers: authHeaders(), params: { target_date: targetDate },
        }),
        fetchVehicles(),
      ]);
      const data = previewRes.data;
      setVariants(data.variants);
      setStats(data.stats);
      setStrictUserIds(data.strict_user_ids || []);
      setVehicles(vehs);
      const initial = data.variants[activeVariant] || data.variants.flex || [];
      setAssignment(initAssignment(initial, vehs));
      if (initial.length === 0) toast.info('Нет подтверждённых заказов на эту дату');
      if (vehs.length === 0) toast.warning('В «Автопарке» нет машин — добавьте их в Настройке контейнеров');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось загрузить маршрут');
    } finally {
      setLoading(false);
    }
  }, [targetDate, activeVariant, fetchVehicles, initAssignment]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const switchVariant = (v) => {
    setActiveVariant(v);
    setAssignment(initAssignment(variants[v] || [], vehicles));
  };

  // All stops currently placed, in column order (used by auto-split / publish)
  const orderedColumns = useMemo(() => {
    const cols = [...vehicles.map((v) => v.id), UNASSIGNED];
    return cols;
  }, [vehicles]);

  const totalPlaced = useMemo(
    () => orderedColumns.reduce((n, k) => n + (assignment[k]?.length || 0), 0),
    [assignment, orderedColumns],
  );

  const moveWithinColumn = (colId, from, to) => {
    const list = assignment[colId] || [];
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setAssignment({ ...assignment, [colId]: next });
  };

  const removeStop = (colId, idx) => {
    const next = [...(assignment[colId] || [])];
    next.splice(idx, 1);
    setAssignment({ ...assignment, [colId]: next });
  };

  const moveToVehicle = (fromCol, idx, toCol) => {
    if (fromCol === toCol) return;
    const fromList = [...(assignment[fromCol] || [])];
    const [item] = fromList.splice(idx, 1);
    const toList = [...(assignment[toCol] || []), item];
    setAssignment({ ...assignment, [fromCol]: fromList, [toCol]: toList });
  };

  // ───── Unified move used by drag & drop ─────
  // Move a stop from (fromCol, fromIdx) to (toCol, toIdx). toIdx === null → append.
  const moveStop = (fromCol, fromIdx, toCol, toIdx) => {
    setAssignment((prev) => {
      const next = { ...prev };
      if (fromCol === toCol) {
        const list = [...(next[fromCol] || [])];
        const item = list[fromIdx];
        if (!item) return prev;
        list.splice(fromIdx, 1);
        let insertAt = toIdx == null ? list.length : toIdx;
        if (fromIdx < insertAt) insertAt -= 1; // compensate for the removed item
        if (insertAt < 0) insertAt = 0;
        if (insertAt > list.length) insertAt = list.length;
        if (insertAt === fromIdx) return prev;
        list.splice(insertAt, 0, item);
        next[fromCol] = list;
        return next;
      }
      const fromList = [...(next[fromCol] || [])];
      const item = fromList[fromIdx];
      if (!item) return prev;
      fromList.splice(fromIdx, 1);
      const toList = [...(next[toCol] || [])];
      let insertAt = toIdx == null ? toList.length : toIdx;
      if (insertAt < 0) insertAt = 0;
      if (insertAt > toList.length) insertAt = toList.length;
      toList.splice(insertAt, 0, item);
      next[fromCol] = fromList;
      next[toCol] = toList;
      return next;
    });
  };

  const handleDragStart = (e, colId, idx, orderId) => {
    dragRef.current = { colId, idx, orderId };
    setDraggingId(orderId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(orderId)); } catch (_) { /* Safari */ }
  };

  const handleDragEnd = () => {
    dragRef.current = null;
    setDraggingId(null);
    setDragOverCol(null);
  };

  // Drop onto a specific stop card → insert before that card
  const handleDropOnStop = (e, toCol, toIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const src = dragRef.current;
    if (src) moveStop(src.colId, src.idx, toCol, toIdx);
    handleDragEnd();
  };

  // Drop onto the column body (empty area) → append to the end
  const handleDropOnColumn = (e, toCol) => {
    e.preventDefault();
    const src = dragRef.current;
    if (src) moveStop(src.colId, src.idx, toCol, null);
    handleDragEnd();
  };

  // Greedy fill biggest-first by container capacity (mirrors backend split_route_by_fleet)
  const autoSplit = () => {
    const allStops = orderedColumns.flatMap((k) => assignment[k] || []);
    if (!allStops.length) return;
    const sorted = [...vehicles].sort(
      (a, b) => (b.capacity_containers || 0) - (a.capacity_containers || 0),
    );
    const cols = {};
    vehicles.forEach((v) => { cols[v.id] = []; });
    cols[UNASSIGNED] = [];
    let remaining = [...allStops];
    for (const v of sorted) {
      if (!remaining.length) break;
      const cap = Math.max(1, v.capacity_containers || 1);
      let load = 0;
      const taken = [];
      while (remaining.length) {
        const s = remaining[0];
        const c = s.containers || 1;
        if (taken.length && load + c > cap) break;
        taken.push(s); load += c; remaining.shift();
      }
      cols[v.id] = taken;
    }
    if (remaining.length) cols[UNASSIGNED] = remaining;
    setAssignment(cols);
    toast.success('Маршрут разбит по вместимости машин');
  };

  const columnLoad = (colId) =>
    (assignment[colId] || []).reduce((n, s) => n + (s.containers || 1), 0);

  const publish = async () => {
    const assignments = vehicles
      .map((v) => ({ vehicle_id: v.id, order_ids: (assignment[v.id] || []).map((s) => s.order_id) }))
      .filter((a) => a.order_ids.length > 0);
    const unassigned = assignment[UNASSIGNED] || [];
    if (unassigned.length > 0) {
      assignments.push({ vehicle_id: null, order_ids: unassigned.map((s) => s.order_id) });
    }
    if (!assignments.length) { toast.error('Маршрут пуст'); return; }
    const overloaded = vehicles.filter(
      (v) => v.capacity_containers && columnLoad(v.id) > v.capacity_containers,
    );
    let msg = `Отправить ${assignments.length} машин(ы) в Telegram-чат логистики?`;
    if (overloaded.length) msg += `\n\n⚠️ Перегружены: ${overloaded.map((v) => v.name).join(', ')}. Всё равно отправить?`;
    if (unassigned.length) msg += `\n\n⚠️ ${unassigned.length} точек без машины.`;
    if (!window.confirm(msg)) return;

    setPublishing(true);
    try {
      const res = await axios.post(`${API}/api/admin/logistics/route-editor/publish`,
        { target_date: targetDate, assignments }, { headers: authHeaders() });
      toast.success(`Отправлено: ${res.data.vehicles} машин(ы), ${res.data.total_stops} точек`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось отправить');
    } finally {
      setPublishing(false);
    }
  };

  const fmtKm = (s) => (s?.km ? `${s.km.toFixed(1)} км` : '—');
  const fmtMin = (s) => {
    if (!s?.minutes) return '—';
    const h = Math.floor(s.minutes / 60); const m = s.minutes % 60;
    return h ? `${h}ч ${m}мин` : `${m}мин`;
  };

  const columnsToRender = [...vehicles.map((v) => v.id),
    ...((assignment[UNASSIGNED] || []).length ? [UNASSIGNED] : [])];

  const vehicleMeta = (colId) => {
    if (colId === UNASSIGNED) return { name: 'Без машины', cap: 0, unassigned: true };
    const v = vehicles.find((x) => x.id === colId);
    return {
      name: v?.name || 'Машина', cap: v?.capacity_containers || 0,
      driver: v?.driver?.name || v?.driver_name || null,
      hasTgDriver: !!v?.driver,
      driverBound: !!v?.driver?.is_bound,
    };
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div>
            <div className="font-semibold text-sm">Редактор маршрута по машинам</div>
            <p className="text-xs text-gray-500">Перетаскивайте точки мышью между машинами и меняйте порядок. По умолчанию все в первой машине.</p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
              className="w-44" data-testid="route-editor-date" />
            <Button variant="outline" onClick={fetchPreview} disabled={loading} data-testid="route-editor-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Загрузить
            </Button>
            <Button variant="outline" onClick={autoSplit} disabled={totalPlaced === 0 || vehicles.length < 2}
              data-testid="route-editor-autosplit">
              <Wand2 className="w-4 h-4 mr-1" />Авто-разбить
            </Button>
            <Button onClick={publish} disabled={publishing || totalPlaced === 0}
              className="bg-emerald-700 hover:bg-emerald-800" data-testid="route-editor-publish">
              <Send className="w-4 h-4 mr-2" />{publishing ? 'Отправляю...' : 'Отправить в Telegram'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Variant selector (initial ordering) */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-gray-500">Стартовый порядок:</span>
            {['strict', 'flex', 'fast'].map((v) => (
              <button key={v} type="button" onClick={() => switchVariant(v)}
                className={`text-left px-3 py-1.5 rounded-lg border-2 transition text-sm ${activeVariant === v ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid={`route-editor-variant-${v}`}>
                <span className="font-medium">{variantLabel[v]}</span>
                <span className="text-xs text-gray-500 ml-2">{variants[v]?.length || 0} • {fmtKm(stats[v])} • {fmtMin(stats[v])}</span>
              </button>
            ))}
            <span className="text-xs text-gray-400 ml-auto">Смена варианта сбрасывает распределение по машинам</span>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle columns */}
      {totalPlaced === 0 ? (
        <Card><CardContent className="text-center py-12 text-gray-400">
          Нет точек. Выберите другую дату или вариант и нажмите «Загрузить».
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="route-editor-columns">
          {columnsToRender.map((colId) => {
            const meta = vehicleMeta(colId);
            const list = assignment[colId] || [];
            const load = columnLoad(colId);
            const over = meta.cap > 0 && load > meta.cap;
            return (
              <Card key={colId} className={`${over ? 'border-red-400' : meta.unassigned ? 'border-amber-300' : ''} ${dragOverCol === colId ? 'ring-2 ring-emerald-400' : ''} transition`}
                data-testid={`vehicle-col-${colId}`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverCol !== colId) setDragOverCol(colId); }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverCol(null); }}
                onDrop={(e) => handleDropOnColumn(e, colId)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <Truck className={`w-4 h-4 shrink-0 ${meta.unassigned ? 'text-amber-500' : 'text-emerald-700'}`} />
                      <span className="truncate">{meta.name}</span>
                    </span>
                    <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full
                      ${over ? 'bg-red-100 text-red-700' : meta.unassigned ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      <Package className="w-3 h-3" />
                      {meta.cap ? `${load}/${meta.cap}` : `${load}`} конт.
                      {over && <AlertTriangle className="w-3 h-3" />}
                    </span>
                  </CardTitle>
                  <div className="text-xs text-gray-400">
                    {list.length} точек{over ? ' • перегруз!' : ''}
                  </div>
                  {!meta.unassigned && (
                    meta.hasTgDriver ? (
                      <div className={`text-[11px] mt-0.5 flex items-center gap-1 ${meta.driverBound ? 'text-emerald-600' : 'text-amber-600'}`}>
                        <User className="w-3 h-3" />
                        {meta.driver}{meta.driverBound ? ' ✓ привязан' : ' • не привязан к TG'}
                      </div>
                    ) : (
                      <div className="text-[11px] mt-0.5 text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> TG-водитель не назначен
                      </div>
                    )
                  )}
                </CardHeader>
                <CardContent className="space-y-2 min-h-[60px]">
                  {list.length === 0 ? (
                    <div className="text-center text-xs text-gray-300 py-4">Перенесите сюда точки</div>
                  ) : list.map((s, idx) => {
                    const isStrict = strictUserIds.includes(s.user_id);
                    return (
                      <div key={s.order_id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, colId, idx, s.order_id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDrop={(e) => handleDropOnStop(e, colId, idx)}
                        className={`p-2 rounded-lg border bg-white cursor-grab active:cursor-grabbing transition ${isStrict ? 'border-amber-300 bg-amber-50' : 'border-gray-200'} ${draggingId === s.order_id ? 'opacity-40 ring-2 ring-emerald-400' : ''}`}
                        data-testid={`stop-${s.order_id}`}>
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5" />
                          <span className="w-5 text-center font-bold text-emerald-700 text-sm shrink-0">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate flex items-center gap-1">
                              <User className="w-3 h-3 text-gray-400 shrink-0" />{s.client_name}
                              {isStrict && <span className="text-amber-600 text-[10px] font-bold">⚠️</span>}
                              <span className="ml-auto text-[10px] text-gray-400">{s.containers || 1} к.</span>
                            </div>
                            <div className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                              <MapPin className="w-3 h-3 shrink-0" />{s.address}
                            </div>
                            <div className="text-[11px] text-gray-500 flex items-center gap-2">
                              {s.delivery_time && (<span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.delivery_time}</span>)}
                              {s.phone && s.phone !== '—' && (<span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mt-1.5">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveWithinColumn(colId, idx, idx - 1)} disabled={idx === 0} data-testid={`up-${s.order_id}`}>
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveWithinColumn(colId, idx, idx + 1)} disabled={idx === list.length - 1} data-testid={`down-${s.order_id}`}>
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                          <Select value={colId} onValueChange={(val) => moveToVehicle(colId, idx, val)}>
                            <SelectTrigger className="h-6 text-xs flex-1" data-testid={`veh-select-${s.order_id}`}>
                              <SelectValue placeholder="Машина" />
                            </SelectTrigger>
                            <SelectContent>
                              {vehicles.map((v) => (
                                <SelectItem key={v.id} value={v.id} className="text-xs">{v.name}</SelectItem>
                              ))}
                              <SelectItem value={UNASSIGNED} className="text-xs">Без машины</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={() => removeStop(colId, idx)} data-testid={`remove-${s.order_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RouteEditorPanel;
